import { useEffect, useState } from "react";
import { Plus, BellPlus, Flame, Sparkles } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { toast } from "sonner";
import CustomizeModal from "@/components/CustomizeModal";
import NotifyBackModal from "@/components/NotifyBackModal";
import { T } from "@/context/TextEditContext";

// Categories where customers get the customize popup (remove ingredients + add extras)
const CUSTOMIZABLE = new Set([
  "Burgers",
  "Sandwiches",
  "Loaded Fries",
  "Wraps",
  "Vegetarian",
]);

/**
 * Human-readable countdown between `now` and `endsAtIso`. Returns "" when
 * the end time has already passed (caller decides what to render then).
 */
function formatCountdown(endsAtIso, now) {
  if (!endsAtIso) return "";
  const end = new Date(endsAtIso).getTime();
  if (Number.isNaN(end)) return "";
  const diff = end - now;
  if (diff <= 0) return "";
  const s = Math.floor(diff / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function MenuItemCard({ item }) {
  const { add } = useCart();
  const { settings } = useSiteSettings();
  const ordersOpen = settings?.orders_open !== false;
  const drinksAreFree = settings?.drinks_are_free_now !== false;
  const [showCustomize, setShowCustomize] = useState(false);
  const [showNotify, setShowNotify] = useState(false);
  // Re-tick every 60s so the LTO countdown badge stays live without a
  // full component re-render storm. `useMemo` derived state (`ltoLive`,
  // `countdown`) recomputes from the tick.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!item.lto_active || !item.lto_ends_at) return;
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, [item.lto_active, item.lto_ends_at]);

  const isDrink = item.category === "Drinks";
  const isAddOn = item.category === "Add-Ons"; // hidden from menu — added inside CustomizeModal
  const isCustomizable = CUSTOMIZABLE.has(item.category);
  // When drinks aren't free right now they act like any other paid menu
  // item — price shown, + button visible, dropped into the cart.
  const drinkIsPaid = isDrink && !drinksAreFree;

  // LTO: live only if the flag is on AND the end date is in the future.
  // After it expires the ribbon disappears (per owner-picked policy the
  // item stays visible — admin dashboard warns them to extend or remove).
  const ltoEndTs = item.lto_ends_at
    ? new Date(item.lto_ends_at).getTime()
    : 0;
  const ltoLive = Boolean(
    item.lto_active && ltoEndTs && ltoEndTs > now
  );
  const promoPrice =
    ltoLive &&
    typeof item.lto_price === "number" &&
    item.lto_price < item.price
      ? item.lto_price
      : null;
  const effectivePrice = promoPrice != null ? promoPrice : item.price;

  const handleClick = () => {
    if (!ordersOpen) {
      toast.error(
        settings?.orders_closed_message ||
          "Online ordering is paused right now."
      );
      return;
    }
    // Pass a copy with the effective price so the cart records the promo
    // amount when LTO is live. Original item is left untouched.
    const cartItem = { ...item, price: effectivePrice };
    if (isCustomizable) {
      setShowCustomize(true);
    } else {
      add(cartItem);
      toast.success(`${item.name} added`, { duration: 1400 });
    }
  };

  return (
    <>
      <div
        data-testid={`menu-item-card-${item.id}`}
        className="bg-white border border-stone-200 rounded-xl overflow-hidden hover:-translate-y-0.5 hover:shadow-md transition-all"
      >
        {item.image_url && !isDrink && !isAddOn && (
          <div className="relative w-full aspect-[16/9] bg-stone-100 overflow-hidden">
            <img
              src={item.image_url}
              alt={item.name}
              loading="lazy"
              decoding="async"
              data-testid={`menu-item-image-${item.id}`}
              className={`w-full h-full object-cover ${
                !item.available ? "grayscale opacity-70" : ""
              }`}
              onError={(e) => {
                // If the image fails to load we hide the whole slot so
                // the card degrades gracefully to the old text layout.
                e.currentTarget.parentElement.style.display = "none";
              }}
            />
          </div>
        )}
        <div className="p-4 flex items-start gap-3">
          <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="font-bold text-stone-800 leading-tight">{item.name}</h3>
            {isDrink && drinksAreFree ? (
              <span
                data-testid={`drink-free-badge-${item.id}`}
                className="text-[10px] uppercase tracking-widest bg-emerald-600 text-white font-black px-2.5 py-1 rounded-full whitespace-nowrap"
              >
                <T k="menu.free_badge" d="Free" />
              </span>
            ) : promoPrice != null ? (
              <div
                data-testid={`lto-price-${item.id}`}
                className="flex items-baseline gap-1.5 whitespace-nowrap"
              >
                <span className="font-mono text-stone-400 text-xs line-through">
                  ${item.price.toFixed(2)}
                </span>
                <span className="font-display text-brand-red text-lg">
                  ${promoPrice.toFixed(2)}
                </span>
              </div>
            ) : (
              <span className="font-display text-brand-red text-lg whitespace-nowrap">
                ${item.price.toFixed(2)}
              </span>
            )}
          </div>
          {(item.is_new || ltoLive) && (
            <div className="flex flex-wrap items-center gap-1 mt-1.5">
              {item.is_new && (
                <span
                  data-testid={`new-badge-${item.id}`}
                  className="inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full"
                >
                  <Sparkles className="w-3 h-3" /> New
                </span>
              )}
              {ltoLive && (
                <span
                  data-testid={`lto-badge-${item.id}`}
                  className="inline-flex items-center gap-1 bg-brand-red text-white text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full animate-ready-glow"
                >
                  <Flame className="w-3 h-3" />
                  <T k="menu.lto_badge" d="Limited" /> · Ends in {formatCountdown(item.lto_ends_at, now)}
                </span>
              )}
            </div>
          )}
          {item.description && (
            <p className="text-xs text-stone-500 mt-1 leading-snug">{item.description}</p>
          )}
          {/* Meal items include fries — controlled per item by the owner. */}
          {item.includes_fries && item.available && (
            <span
              data-testid={`fries-included-tag-${item.id}`}
              className="inline-flex items-center gap-1 mt-2 bg-brand-yellow text-stone-900 text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full"
            >
              🍟 <T k="menu.fries_included" d="Fries included" />
            </span>
          )}
          {isDrink && drinksAreFree && item.available && (
            <p className="text-[11px] text-emerald-700 mt-1 font-bold">
              Pick your free drink at checkout →
            </p>
          )}
          {isCustomizable && item.available && (
            <p className="text-[11px] text-brand-red/80 mt-1 font-bold">
              <T k="menu.customize_note" d="Tap + to customize" />
            </p>
          )}
          {isAddOn && (
            <p className="text-[11px] text-stone-500 mt-1 italic">
              <T k="menu.addon_note" d="Available as an add-on when you customize a meal" />
            </p>
          )}
          {!item.available && !isAddOn && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-widest bg-stone-200 text-stone-600 px-2 py-0.5 rounded-full">
                <T k="menu.sold_out" d="Sold out" />
              </span>
              <button
                type="button"
                onClick={() => setShowNotify(true)}
                data-testid={`notify-back-btn-${item.id}`}
                className="text-[10px] uppercase tracking-widest bg-brand-red text-white font-black px-2.5 py-1 rounded-full inline-flex items-center gap-1 hover:bg-red-800 active:scale-95 transition-all"
              >
                <BellPlus className="w-3 h-3" />
                <T k="menu.notify_btn" d="Text me when back" />
              </button>
            </div>
          )}
          {!item.available && isAddOn && (
            <span className="inline-block mt-2 text-[10px] uppercase tracking-widest bg-stone-200 text-stone-600 px-2 py-0.5 rounded-full">
              Sold out
            </span>
          )}
        </div>
        {(!isDrink || drinkIsPaid) && !isAddOn && (
          <button
            onClick={handleClick}
            disabled={!item.available || !ordersOpen}
            data-testid={`add-to-cart-btn-${item.id}`}
            title={!ordersOpen ? "Online ordering is paused" : undefined}
            className="shrink-0 w-11 h-11 rounded-full bg-brand-red text-white flex items-center justify-center hover:bg-red-800 disabled:opacity-30 disabled:cursor-not-allowed transition-transform active:scale-90 shadow-md"
            aria-label={`Add ${item.name}`}
          >
            <Plus className="w-5 h-5" />
          </button>
        )}
        </div>
      </div>

      {showCustomize && (
        <CustomizeModal
          item={{ ...item, price: effectivePrice }}
          onClose={() => setShowCustomize(false)}
        />
      )}

      {showNotify && (
        <NotifyBackModal item={item} onClose={() => setShowNotify(false)} />
      )}
    </>
  );
}
